import { Injectable, Signal, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';

import * as AuthSelectors from '../auth/store/auth.selectors';

/**
 * When each half of the shell chrome has everything it needs to paint real
 * content (NBO-2331 r4). Both halves read THIS service, so the rail and the
 * top bar can never disagree about what "loading" means, and the loading
 * vocabulary lives in one place instead of two component pipes.
 *
 * Two signals rather than one, deliberately, because the two halves wait on
 * different things and they arrive far apart:
 *
 * - `navReady` — the role claim off the Firebase ID token. Local and fast.
 * - `identityReady` — adminUser + activeTenant, which come from the
 *   AdminUserTenants GraphQL query. A network round trip, measured seconds
 *   behind the roles on a cold load.
 *
 * Gating the rail on `identityReady` too would have given one synchronised
 * reveal, but it would also have held navigation hostage to a query it does
 * not need — the user could have navigated seconds earlier. Availability wins
 * over visual symmetry; each half still reveals as ONE group, in one fade.
 */
@Injectable({ providedIn: 'root' })
export class ChromeReadyService {
  private readonly store = inject(Store);

  private readonly rolesResolved = toSignal(this.store.select(AuthSelectors.selectRolesResolved), {
    initialValue: false
  });
  private readonly adminUser = toSignal(this.store.select(AuthSelectors.selectAuthAdminUser), {
    initialValue: null
  });
  private readonly activeTenant = toSignal(this.store.select(AuthSelectors.selectActiveTenant), {
    initialValue: null
  });

  /**
   * The rail's tree is a static, code-owned structure — the only thing it
   * waits for is which items the user may SEE. Hence the resolved flag, not
   * `roles.length`: an empty role set is a real answer.
   */
  readonly navReady: Signal<boolean> = computed(() => this.rolesResolved());

  /** Tenant name, user name and initials — the top bar's identity cluster. */
  readonly identityReady: Signal<boolean> = computed(() => !!this.adminUser() && !!this.activeTenant());
}
